{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "title": "Way Experience Taxonomy",
  "description": "Classification framework for categorizing and tagging experiences on the platform.",
  "domains": {
    "category": {
      "label": "Category",
      "description": "What the listing is about. Applies only to guest experiences. Return an empty array for utility or operational services (e.g. parking, airport transfers, storage) that are not guest experiences.",
      "values": [
        {
          "key": "adventure",
          "name": "Adventure",
          "description": "High-intensity, thrill-seeking, or physically demanding experiences where the challenge is the point"
        },
        {
          "key": "outdoor",
          "name": "Outdoor",
          "description": "Nature-based experiences where the setting is the draw, not the physical intensity"
        },
        {
          "key": "art",
          "name": "Art",
          "description": "Creative expression through visual arts, craft, music, and maker practices."
        },
        {
          "key": "culture",
          "name": "Culture",
          "description": "Explores living traditions, practices, and creative expressions of a place"
        },
        { "key": "culinary", "name": "Culinary", "description": "Centered around eating, drinking, or culinary discovery" },
        { "key": "family", "name": "Family", "description": "Designed for shared enjoyment across ages and generations" },
        {
          "key": "history",
          "name": "History",
          "description": "Immerses guests in the stories, people, and places of the past"
        },
        { "key": "wellbeing", "name": "Wellbeing", "description": "Focused on health, mindfulness, or physical well-being" },
        {
          "key": "entertainment",
          "name": "Entertainment",
          "description": "Engaging performances, shows, or experiences designed to delight"
        },
        {
          "key": "skillbuilding",
          "name": "Skillbuilding",
          "description": "Hands-on learning where guests leave with a new ability or craft"
        }
      ]
    },
    "wellbeing_modality": {
      "label": "Wellbeing Modality",
      "description": "Specific type of wellbeing experience.",
      "parent": { "domain": "category", "value": "wellbeing" },
      "values": [
        {
          "key": "breathwork",
          "name": "Breathwork",
          "description": "Guided breathing practices to calm the nervous system and restore mental clarity"
        },
        {
          "key": "facial",
          "name": "Facial",
          "description": "Skin-focused treatments that cleanse, restore, and rejuvenate the face"
        },
        {
          "key": "fitness_training",
          "name": "Fitness Training",
          "description": "Structured, coach-led workouts designed to build strength, endurance, or athleticism"
        },
        {
          "key": "massage",
          "name": "Massage",
          "description": "Hands-on bodywork that relieves tension, aids recovery, and promotes deep relaxation"
        },
        {
          "key": "meditation",
          "name": "Meditation",
          "description": "Guided or silent practices to quiet the mind and cultivate inner stillness"
        },
        {
          "key": "pilates",
          "name": "Pilates",
          "description": "Low-impact, precision-based movement focused on core strength and body alignment"
        },
        {
          "key": "contrast_therapy",
          "name": "Contrast Therapy",
          "description": "Heat and thermal-based therapies including sauna, steam, hot immersion, and contrast experiences designed to detoxify, improve circulation, and deeply relax the body."
        },
        {
          "key": "sound_bath",
          "name": "Sound Bath",
          "description": "Immersive acoustic experiences using resonant instruments to induce a meditative state"
        },
        {
          "key": "yoga",
          "name": "Yoga",
          "description": "Movement and breathwork practices that unite body, mind, and spirit through intentional flow"
        }
      ]
    },
    "activity_type": {
      "label": "Activity Type",
      "description": "How the experience is delivered. Applies to all listings regardless of category.",
      "values": [
        {
          "key": "guided_activity",
          "name": "Guided Activity",
          "description": "A hosted experience where a guide leads guests through an activity in real time"
        },
        {
          "key": "class",
          "name": "Class",
          "description": "Structured instruction with a defined skill or technique as the intended takeaway"
        },
        {
          "key": "tasting",
          "name": "Tasting",
          "description": "A curated sampling experience focused on flavor, beverage, or sensory exploration"
        },
        { "key": "tour", "name": "Tour", "description": "A guided journey through a place, story, or collection of stops" },
        {
          "key": "dining_event",
          "name": "Dining Event",
          "description": "A sit-down dining experience, often themed or chef-driven, designed as a special occasion meal"
        },
        {
          "key": "access_pass",
          "name": "Access Pass",
          "description": "Entry or access to a space, event, or amenity not otherwise available to the public"
        },
        {
          "key": "rental",
          "name": "Rental",
          "description": "Use of a space, equipment, or vehicle for a defined period of time"
        },
        {
          "key": "general_admission",
          "name": "General Admission",
          "description": "A performance or show experienced in real time by an audience"
        },
        {
          "key": "brand_collaboration",
          "name": "Brand Collaboration",
          "description": "A curated experience or pop-up brought to life in partnership with a consumer brand, from trunk shows to exclusive product moments"
        },
        {
          "key": "amenity",
          "name": "Amenity",
          "description": "A curated add-on delivered to your room or arranged ahead of arrival to personalize your stay"
        },
        {
          "key": "service",
          "name": "Service",
          "description": "A professional service performed on behalf of the guest, leaving the details, logistics, or craft entirely in expert hands"
        }
      ]
    },
    "rental_asset_type": {
      "label": "Rental Asset Type",
      "description": "Specific type of rental.",
      "parent": { "domain": "activity_type", "value": "rental" },
      "values": [
        {
          "key": "lounge_chair",
          "name": "Lounge Chair",
          "description": "A single reserved outdoor seat, typically at a pool or beach, for daytime relaxation. Includes beach chairs and pool chairs."
        },
        {
          "key": "bike",
          "name": "Bike",
          "description": "A bicycle or e-bike rented for self-guided travel or exploration around the property or surrounding area."
        },
        {
          "key": "boat",
          "name": "Boat",
          "description": "A motorized or sailboat watercraft rented for on-water use. Larger than a kayak or paddleboard; may include a captain or be self-operated."
        },
        {
          "key": "private_cabana",
          "name": "Private Cabana",
          "description": "A semi-private, shaded structure or enclosure with seating, typically at a pool or beach, offering a more premium and sheltered experience than a lounge chair or daybed."
        },
        {
          "key": "equipment",
          "name": "Equipment",
          "description": "A specific item, device, or kit rented for personal use. Includes wellness devices (e.g. Theragun), cameras, sports gear, or any standalone tool not covered by another rental type."
        },
        {
          "key": "golf_cart",
          "name": "Golf Cart",
          "description": "A small electric or gas-powered cart rented for transportation around a property, resort, or course."
        },
        {
          "key": "kayak",
          "name": "Kayak",
          "description": "A narrow, human-powered watercraft for one or two people, used for calm water exploration."
        },
        {
          "key": "paddleboard",
          "name": "Paddleboard",
          "description": "A flat, buoyant board rented for stand-up paddleboarding on calm or open water."
        },
        {
          "key": "venue",
          "name": "Venue",
          "description": "A private space such as a room, rooftop, lawn, or event area rented for personal gatherings, events, or exclusive use."
        }
      ]
    },
    "service_type": {
      "label": "Service Type",
      "description": "Specific type of service.",
      "parent": { "domain": "activity_type", "value": "service" },
      "values": [
        {
          "key": "transportation",
          "name": "Transportation",
          "description": "A private or arranged vehicle service for transfers, excursions, or on-demand travel. Includes car service, airport transfers, and chauffeured outings."
        },
        {
          "key": "spa_treatment",
          "name": "Spa Treatment",
          "description": "A hands-on therapeutic or beauty service performed by a trained practitioner. Includes massage, facials, body treatments, and other spa services delivered in a spa or in-room setting."
        },
        {
          "key": "private_chef",
          "name": "Private Chef",
          "description": "A culinary service where a professional chef prepares and serves a meal on behalf of the guest, typically in a private setting such as a villa, suite, or event space."
        },
        {
          "key": "photography",
          "name": "Photography",
          "description": "A professional photo or video service capturing moments during a guest's stay, experience, or special occasion. Includes portrait sessions, event coverage, and adventure photography."
        },
        {
          "key": "training",
          "name": "Training",
          "description": "A personalized coaching or instruction session focused on physical fitness, skill development, or sport. Distinct from a Class in that it is one-on-one or small-group and tailored to the individual."
        },
        {
          "key": "childcare",
          "name": "Childcare",
          "description": "Supervised care for children provided by trained staff, including babysitting, kids club programming, and in-room childcare services."
        },
        {
          "key": "styling",
          "name": "Styling",
          "description": "A personal appearance service including hair, makeup, tailoring, and personal shopping, performed by a professional on behalf of the guest."
        }
      ]
    },
    "age_policy": {
      "label": "Age Policy",
      "description": "Who the experience is designed for based on age suitability.",
      "values": [
        {
          "key": "adults_only",
          "name": "Adults Only",
          "description": "Restricted to adult guests; not appropriate or available for children"
        },
        { "key": "all_ages", "name": "All Ages", "description": "Open and suitable for guests of any age" },
        {
          "key": "kids_only",
          "name": "Kids Only",
          "description": "Designed specifically for children; not intended for adult participation"
        }
      ]
    },
    "physical_intensity": {
      "label": "Physical Intensity",
      "description": "The level of physical effort required to participate in the experience.",
      "values": [
        {
          "key": "low",
          "name": "Low",
          "description": "Minimal physical effort required; accessible to most guests regardless of fitness level"
        },
        {
          "key": "moderate",
          "name": "Moderate",
          "description": "Some physical activity involved; guests should be reasonably mobile and comfortable with light exertion"
        },
        {
          "key": "high",
          "name": "High",
          "description": "Demanding physical activity; guests should be in good health and prepared for significant exertion"
        }
      ]
    },
    "seasonality": {
      "label": "Seasonality",
      "description": "When the experience is typically available or most relevant. May be multi-select.",
      "multiSelect": true,
      "values": [
        {
          "key": "year_round",
          "name": "Year Round",
          "description": "Available consistently throughout the year with no seasonal dependency"
        },
        {
          "key": "winter_peak",
          "name": "Winter Peak",
          "description": "Most relevant or exclusively available during winter months"
        },
        {
          "key": "summer_peak",
          "name": "Summer Peak",
          "description": "Most relevant or exclusively available during summer months"
        },
        {
          "key": "holiday_peak",
          "name": "Holiday Peak",
          "description": "Tied to a specific holiday period such as Thanksgiving, Christmas, or New Year"
        },
        {
          "key": "shoulder_season",
          "name": "Shoulder Season",
          "description": "Best experienced during the transitional periods between peak seasons"
        }
      ]
    },
    "weather_dependency": {
      "label": "Weather Dependency",
      "description": "Whether the experience is affected by outdoor weather conditions.",
      "values": [
        {
          "key": "weather_sensitive",
          "name": "Weather Sensitive",
          "description": "Takes place outdoors or is significantly impacted by weather; may be subject to cancellation or modification"
        },
        {
          "key": "weatherproof",
          "name": "Weatherproof",
          "description": "Takes place indoors or is otherwise unaffected by weather conditions"
        }
      ]
    },
    "risk": {
      "label": "Risk",
      "description": "The level of inherent safety risk associated with the experience.",
      "values": [
        {
          "key": "low",
          "name": "Low",
          "description": "No meaningful safety risk; suitable for all guests without special precautions"
        },
        {
          "key": "medium",
          "name": "Medium",
          "description": "Some safety considerations apply; guests may need to sign a waiver or follow safety guidelines"
        },
        {
          "key": "high",
          "name": "High",
          "description": "Significant safety risk involved; may include activities using watercraft or aircraft that require trained operation."
        }
      ]
    }
  }
}
